/**
 * The browser half of contract ① (blueprint#ui): JSON over HTTP + one SSE
 * stream to the workbench process. The browser never talks to the core.
 */

import type { Attachment } from "../components/attachments/kind";
import { OWNER_AUDIENCE, type Audience, type PanelSpec, type RecordState } from "../panel/spec";
import { apiPath } from "./base";
import { withDeadline } from "./deadline";
import { surfaceDiagnostic, surfaceResources, type SurfacePage } from "./surfaceResources";

export interface ScreenPage {
  showing: string; panel_key: string; block: number; context: Record<string, string>; session_id?: string;
}
export interface ScreenReport {
  tab: string; revision: number; showing: string[]; pages?: ScreenPage[]; session_id?: string; left?: boolean; content_unavailable?: boolean;
}
let prepareChatScreen: ((sessionId: string) => Promise<ScreenReport | undefined>) | undefined;
/** The live shell supplies identity just before chat; API-only clients have no screen. */
export function setChatScreenReporter(reporter: typeof prepareChatScreen): () => void {
  prepareChatScreen = reporter;
  return () => { if (prepareChatScreen === reporter) prepareChatScreen = undefined; };
}

export interface ApiErrorBody {
  title: string;
  detail: string;
  hint: string | null;
  /**
   * Who wrote these three sentences for whom (§7.183).
   *
   * Absent means `developer`: a body that has not said is one written by a
   * server that does not know who is looking at the page. It is the sentences
   * that know, so they say it -- see `ApiError.machine` below for what the
   * shell used to do instead.
   */
  audience?: Audience;
  /**
   * What a page can branch on, when the route declared one (§7.133's block):
   * a `code` such as `configuration_changed`, and the `key` of the field the
   * sentence is about, so the page can put that field's own name beside it.
   */
  code?: string;
  key?: string;
}

/** Every failure carries the three lines the UI shows: what / why / what now. */
export class ApiError extends Error {
  constructor(
    public title: string,
    public detail: string,
    public hint: string | null,
    public status: number,
    /**
     * Did these three sentences come off the wire, or did this shell write
     * them?
     *
     * The ones written here are Chinese sentences somebody wrote for a person
     * to read. The ones that arrive in a response body were written by a
     * server that does not know who is looking at the page: they name panels,
     * the core, sockets and exception classes -- and `ErrorState` used to draw
     * them wherever it was called, including on Owner pages (the report's
     * D297). So the two are told apart at the one place where the difference
     * is known, which is where the failure is made.
     *
     * For a body off the wire that one place is the body's own `audience`, not
     * the status code: the Hub's 503 for a stopped Agent carries three
     * sentences written to the owner (「你在设置里停止了它」), and marking every
     * non-2xx the machine's replaced them with 「这一块暂时没有读到」 on the one
     * page an owner goes to when their Agent is not answering (D768).
     */
    public machine = false,
  ) {
    super(`${title}: ${detail}`);
    this.name = "ApiError";
  }

  /** See `ApiErrorBody.code`; set only off a body that carried one. */
  code?: string;
  key?: string;

  static fromUnknown(err: unknown): ApiError {
    if (err instanceof ApiError) return err;
    // The shell also raises failures locally (a malformed PanelSpec, a block
    // pointing at a source that is not there). They carry the same three lines.
    if (
      typeof err === "object" &&
      err !== null &&
      typeof (err as ApiErrorBody).title === "string"
    ) {
      const body = err as ApiErrorBody;
      return new ApiError(body.title, body.detail ?? "", body.hint ?? null, 0);
    }
    return new ApiError(
      "请求没有完成",
      err instanceof Error ? err.message : String(err),
      "这是浏览器到 workbench 进程这一跳的问题。确认 workbench 还在跑。",
      0,
      true,
    );
  }
}

export type ConnectionState = "ready" | "connecting" | "disconnected";

export interface Connection {
  state: ConnectionState;
  error: string | null;
  endpoint: string;
  connected_at: number | null;
  generation: number;
  cursor: string | null;
  events_seen: number;
  /** Consecutive failed attempts to get a tail running; 0 when healthy. */
  retry_attempts: number;
  retry_reason: string | null;
}

/**
 * A `slot: record` panel.  When the timeline meets an event it claims, that row
 * is drawn with the blocks it declares; when the conversation carries a call it
 * claims, the card there is its own (`panel/records.tsx`) -- which is why the
 * shell needs the whole spec, with no other page to go and fetch it from.
 */
export interface RecordPanel {
  key: string;
  plugin: string;
  /** The registry's own id for this registration, e.g. `gen-4`.
   *  Opaque: the real core mints it and there is no ordinal behind it,
   *  so it belongs in a tooltip, never in prose that counts. */
  generation_id: string | null;
  /** An event kind, a prefix of its own tools ending in a dot, or
   *  `tool:` and one of its own tools. */
  renders: string[];
  spec: PanelSpec;
}

export interface PanelInteraction {
  request_id: string; session_id: string; version: string;
  /** The original asking call; used only with session_id to link its timeline trace. */
  source_call_id?: string;
  placement: "page" | "card" | "sidebar" | "dialog"; status?: string;
  /** When the Agent asked, in seconds; a list of requests says since when without asking anything else. */
  asked_at?: number;
  /** Whether the owner can read the conversation the Agent raised it in. False for one raised where the console does not
   *  speak (a schedule's): sending the owner back there shows an empty conversation. Absent: treat as readable. */
  owner_conversation?: boolean;
}

export interface PanelPresentation {
  key: string; generation_id: string; plugin: string; title: string;
  request_id: string; session_id: string;
  panel_key: string; panel_generation: string; context: Record<string, string>;
  placement: "sidebar" | "card" | "dialog"; created_at: number;
}

export interface PanelEntry {
  /** Plugin-provided content for the generic first-use location. */
  bootstrap?: boolean;
  /** This active plugin contributes content to an Agent's overview. */
  overview?: boolean;
  overview_preview?: { read_index: number };
  interaction?: PanelInteraction | null;
  key: string;
  plugin: string;
  /** The registry's own id for this registration, e.g. `gen-4`.
   *  Opaque: the real core mints it and there is no ordinal behind it,
   *  so it belongs in a tooltip, never in prose that counts. */
  generation_id: string | null;
  title: string;
  icon: string | null;
  slot: PanelSpec["slot"];
  /** Who the page is for (§7.183): the server lists only the owner's pages
   *  while the developer switch is off, and says so on every row. */
  audience?: "owner" | "developer";
  order: number | null;
  description: string | null;
  /** File provenance supplied by /api/surface-pages, matched to this registration. */
  origin?: "local" | "shipped" | "unknown";
  workspace?: { key?: string; role?: "entry" | "support" | "template"; title?: string; [key: string]: unknown } | null;
  surface_template?: { key: string; title: string; ui: string; description?: string } | null;
  surface_instance?: { template_panel: string; template_key: string } | null;
  /** The namespace a generated config page renders, `metadata.configures`. */
  configures?: string | null;
  /** How this settings destination wants to be found — `connections/directory.ts`
   *  reads it, and it is the plugin's own `settings:` declaration. */
  settings?: { [key: string]: unknown } | null;
}

export interface PanelProblem {
  key: string;
  problem: string;
}

export interface SessionSummary {
  id: string;
  last_ts: string;
  message_count: number;
  principal_id: string | null;
  preview: string;
}

export interface Principal {
  id: string;
  display_name: string;
  /** owner | guest -- whether this person owns the machine; it names them and filters nothing. */
  role: string;
  channels?: string[];
  revoked?: boolean;
}

export interface Bootstrap {
  /** Ordinary messages can be durably accepted while this Runtime is unavailable. */
  message_delivery?: boolean;
  version: string;
  connection: Connection;
  panels: PanelEntry[];
  presentations?: PanelPresentation[];
  surface_versions?: Record<string, string>;
  record_panels: RecordPanel[];
  panel_problems: PanelProblem[];
  sessions: SessionSummary[];
  prefs: Record<string, unknown>;
  spend: SpendAlert | null;
  ledgers: string[];
  principals: Principal[];
  owner: OwnerState;
  account_id: string;
  db_path: string;
  rebuilt_at: number | null;
  /**
   * How many `command`/`work` frames this process has ever broadcast. A
   * poll-only workspace (`?workspace=embedded` or a stream reported down)
   * compares this against the value its last poll saw; any change -- not
   * only a larger one, since a restarted server starts counting at 0 again
   * -- means something this shell cares about was said since then
   * (`liquid-page-runtime.md` §1). A page with a live stream never reads it.
   */
  told: number;
}

/** ``Failure(code, message, retryable, details)`` -- what handle.fail carried. */
export interface Failure {
  code: string;
  message: string;
  retryable: boolean;
  details: Record<string, unknown>;
}

/**
 * A failure said by whoever knew what it was.
 *
 * Not a shape this shell reads out of prose: `kind` is the failing side's own
 * name for the class of failure, `sentence` is one line it wrote for the Owner,
 * and `missing` names what is not there in the words a person uses for those
 * things -- 「配音服务的密钥」, never `TTS_API_KEY`. Whoever fails declares it,
 * because whoever fails is the only party that knows: a run failed with 「缺口：
 * 本 Producer 缺 TTS_API_KEY」 in its own error text while the overview said it
 * could not tell why (D661), and the shell guessing from that text is how the
 * core's own deadline sentence came out as 「暂未确认」 (D372).
 *
 * Optional on every row that may carry it, and absent is an ordinary answer: the
 * sentence for an undeclared failure is the admission that nobody wrote one.
 */
export interface DeclaredFailure {
  kind: string;
  sentence: string;
  missing: string[];
}

/**
 * What the shell reads off `effect.released`: `ok`, in two words.
 *
 * Two, not three.  `expired` was the third and it cannot be produced any more:
 * since §7.140 a deadline is the holder calling `handle.fail`, which comes down
 * the tail as an ordinary refusal with the holder's own sentence in `summary`.
 * A card that kept the word would have to invent it from a reason string.
 */
export type ApprovalVerdict = "approve" | "deny";

export interface OwnerState {
  /** null while the current connection's ownership records are being replayed. */
  present: boolean | null;
  principal_id: string | null;
  display_name: string | null;
  /** How many people, folded across their seats. */
  principals: number;
  /** How many live `identities` entries -- one per seat, revoked ones dropped. */
  identities: number;
}

/**
 * One button on a reply.  §7.139 ⑤: the shape carries no approval meaning.
 *
 * `approves` is the whole of what an option says about itself, and it is the
 * plugins' own word (a reply option): true for
 * the option that goes ahead.  There is no `tone` here -- the shell used to
 * read one and no plugin has ever written one.
 */
export interface ReplyOption {
  value: string;
  label: string;
  approves?: boolean;
}

/** A page's first words and optional ordinary messages the Owner can choose. */
export interface ConversationOpening {
  text: string;
  options?: Pick<ReplyOption, "label" | "value">[];
}

/**
 * One approval card, assembled here rather than delivered.
 *
 * CHARTER §7.139 ⑤ took the approval message away: what arrives is an ordinary
 * reply that happens to carry options. This console draws a card when it can
 * put that reply beside an effect the core is still holding — the two halves
 * are matched by session, because nothing on the reply names the effect. So a
 * card can have a question half and no machine half, and it says which it has
 * instead of pretending to both.
 */
export interface ApprovalCard {
  /** Stable key supplied by the approval projection; a real hold uses `effect:<effect_id>`. */
  key: string;
  session_id: string;
  /** Whether the owner can read that conversation here. False for one the Agent holds for itself (the operating
   *  loop's, a schedule's): a way back to it shows an empty conversation. Absent: treat as readable. */
  owner_conversation?: boolean;
  ts: number | string;
  title: string;
  summary: string;
  /** Who put the asking reply there: the `decision.fold` consumer (§7.139 ①). */
  asked_by: string;
  /** ApprovalRequest.category: which question the person is being asked. */
  category: string;
  /** ApprovalRequest.effect_class: what kind of thing this is. */
  effect_class: string;
  /** ApprovalRequest.level: how much of the world it touches (read/write/external/paid). */
  level: string;
  /** Still unanswered when the core restarted, so it was asked again. */
  reoffered: boolean;
  tool: string | null;
  arguments: Record<string, unknown>;
  review?: { title?: string; summary?: string; policy_reason?: string; presentation?: Record<string, unknown> };
  effect_id: string | null;
  turn_id: string | null;
  /** The handle the effect parked on, or "" when this card has no machine half. */
  handle: string;
  /**
   * The consumer holding the call, off `effect.held` (§7.140).  Empty means the
   * row does not say -- a database written before the column existed -- and
   * then nobody can be named as able to let go, so no button is offered.
   */
  held_by: string;
  /** Why it is waiting: the holder's own sentence, carried and not read. */
  detail: string;
  options: ReplyOption[];
  resolved: boolean;
  resolution: ApprovalVerdict | string | null;
  resolved_at: number | string | null;
  decided_by: string | null;
  /**
   * The seat a person answered from before they had a principal -- the
   * first boot, where the press is the claim.  `decided_by` is null there,
   * and this is what keeps the page from saying nobody looked.
   */
  answered_from: string | null;
  reason: string | null;
  failure: Failure | null;
  /** Original effect's uniquely linked request and its actual continuation result. */
  command_result?: Pick<CommandStatus, "obligation_id" | "source" | "action_state" | "error"> | null;
}

/**
 * What a record's card is handed: read #0 of its blocks
 * (`workbench/server/records.py`, `liquid-page-runtime.md` §9).
 *
 * The same record facts for every tool, all derived from the core's own rows, so
 * no card can show a state the record does not have. `result` is the call's
 * latest answer; `child` is there when the call handed its work to a child
 * turn, with the spawn's `metadata` passed on as the spawner wrote it. Of
 * `arguments`, `result` and `child.metadata`, only the paths the panel names
 * are here, where they stood in the record.
 */
export interface RecordRead {
  tool: string;
  arguments: Record<string, unknown>;
  result: Record<string, unknown> | null;
  state: RecordState;
  child: { session_id: string; metadata: Record<string, unknown> } | null;
  /** The incoming that returned handed-off work, only when the card asks to read it. */
  settlement?: { text?: string } | null;
}

/** One claimed call, carried on the bubble that stands where it was made. */
export interface ConversationRecord {
  /** The panel that draws it, decided when the conversation was read. */
  panel: string;
  read: RecordRead;
}

/**
 * One bubble.
 *
 * `kind` says where it came from, and since §7.139 that is the whole story: a
 * `message` is an `l0.inbound` row on this channel, a `reply` is a `tool_call`
 * of `workbench.reply` that some `decision.fold` consumer put in the decision,
 * and a `stream` is the `llm.chunk` preview running ahead of both. A `record`
 * is any other call made here that a plugin's record panel claims, standing
 * where the call was made, with no words of its own. Nothing was delivered to
 * this page; it was folded out of the ledger, which is why a reload brings the
 * same conversation back.
 */
export interface ChatMessage {
  explanation?: string | null;
  choice?: { reply_id: string; value: string; label: string } | null;
  id: string;
  session_id: string;
  role: "user" | "agent";
  text: string;
  ts: number | string;
  kind: "message" | "reply" | "stream" | "record";
  obligation_id: string | null;
  /** The reply call this bubble is, when it is one. */
  call_id: string | null;
  effect_id: string | null;
  /** The model request whose chunks are filling it in, while they are. */
  request_id: string | null;
  turn_id: string | null;
  streaming: boolean;
  /**
   * Who asked for this reply: the fold consumer's plugin name, or `model` /
   * `preset` (§7.139 ①). The fact the old outbox row could not state — a
   * message the model never asked for looked exactly like one it did.
   */
  author: string | null;
  /** §7.139 ⑤: buttons. Pressing one submits its value as an ordinary input. */
  options: ReplyOption[];
  /**
   * The files this message carried (`pa/contracts/messages.py` `Attachment`).
   *
   * On a reply they are what this turn's tools produced, collected onto the
   * call by the fold; on a message they are what a person sent in. The shell
   * draws a card per media type and never opens the file itself — the bytes
   * come from one route (`/api/attachments/<the file's own URI>`), and so
   * does the size a card prints.
   */
  attachments: Attachment[];
  /**
   * sending | sent | failed, for a reply; unanswered, for a message whose turn
   * failed without saying anything back. `null` for anything else.
   */
  state: string | null;
  error: string | null;
  /**
   * On an unanswered message a plugin ended on purpose: the reason it wrote
   * for the person. The owner's page shows this and never `error`.
   */
  notice?: string | null;
  external_id: string | null;
  payload: { approval?: ApprovalCard; record?: ConversationRecord } | null;
  /**
   * On a `stream` bubble only: which telling of it this is (`ChatFrame`). A
   * read carries the number it is as new as, so a page appends a growth only
   * onto the telling just before it.
   */
  stream_seq?: number;
}

/**
 * §7.77's other half: an effect the core is holding, straight off the ledger.
 *
 * A card is a question somebody asked; this is what is actually blocked. They
 * are listed separately because the join between them is by session and can
 * fail — and an effect nobody can see is worse than one drawn without its words.
 */
export interface HeldEffect {
  effect_id: string;
  handle: string;
  tool: string | null;
  level: string;
  category: string;
  /** The `tool.before_call` consumer holding it -- the only party that may let go. */
  held_by: string;
  session_id: string | null;
  /** As on `ApprovalCard`: whether the owner can read that conversation here. */
  owner_conversation?: boolean;
  turn_id: string | null;
  call_id: string | null;
  intent_hash: string | null;
  ts: number | null;
  detail: string | null;
}

export interface TurnProgress {
  obligation_id: string;
  turn_id?: string | null;
  dispatch_mode?: string | null;
  state: string;
  round: number;
  tool: string | null;
  since: number | string | null;
}

export interface MessageDelivery {
  id: string;
  state: "queued" | "waiting_capacity" | "owner_off" | "maintenance" | "retrying" | "delivered" | "rejected";
  reason?: string | null;
}

export interface ChatReceipt {
  command_id: string;
  obligation_id: string | null;
  delivery?: MessageDelivery;
}

export interface ChatStopStatus {
  command_id: string;
  obligation_id: string | null;
  session_id: string;
  turn_id: string;
  received: boolean;
  accepted: boolean;
  /** Original stop input time from its admitted obligation. */
  requested_at: number | null;
  request_status: string | null;
  reason: string | null;
  terminal: boolean;
  outcome: string | null;
  in_flight: boolean | null;
  awaiting_receipts: { model_request_ids: string[]; tool_call_ids: string[] };
  error: string | null;
  stop: Record<string, unknown> | null;
  turn_final_ref: string | null;
  scope: "top_level_turn";
  background_work: "not_stopped";
}

/**
 * One tail event, in the shape the server reads the contract into.
 *
 * The core gives `{cursor, ref, kind, created_at, payload}`, with session and
 * principal as columns *inside* the payload; `ledger` is derived from `kind`,
 * and `row` is the payload.
 */
export interface LedgerEvent {
  cursor: string;
  /** float seconds -- the contract's Timestamp is a float. */
  ts: number | null;
  ref: string;
  ledger: string;
  kind: string;
  plugin: string | null;
  session_id: string | null;
  principal_id: string | null;
  row: Record<string, unknown>;
  /**
   * `false` when the server keeps this event without part of its row -- a
   * model request's context. The whole event is read by its ref (`api.event`).
   */
  whole?: boolean;
}

export interface LedgerRow extends Record<string, unknown> {
  id?: string;
  state?: string;
  _tone?: string;
  _ts?: number | string;
  _cursor?: string;
  _plugin?: string;
}

export interface TimelineResponse {
  events: LedgerEvent[];
  facets: Record<string, string[]>;
  total_seen: number;
}

export interface SourceResult {
  type: "tail" | "read";
  state: "ready" | "error" | "pending";
  error?: string;
  code?: string;
  tool?: string;
  obligation_id?: string;
  read_key?: string;
  command_id?: string;
  rows?: Record<string, unknown>[] | null;
  value?: unknown;
  /**
   * The version of this answer, at the path `PanelSource.version` named.
   *
   * Absent when the declaration named none, and that is not the same as a
   * version of `null`: a form over a read with no version cannot tell its own
   * edit from somebody else's and has to say so rather than overwrite
   * (`liquid-page-runtime.md` §3). Read with `"version" in source`.
   */
  version?: unknown;
  /** `pending` only: why it is not here yet. */
  detail?: string;
}

/**
 * The last piece of work one surface started that actually finished.
 *
 * `task` is the person's own words or a button's own label, never a tool key;
 * `turn_id` is what the timeline filters on. The whole object is absent when
 * the server cannot honestly name one (`workbench/server/surface_requests.py`).
 */
export interface SurfaceChange {
  task: string;
  session_id: string | null;
  obligation_id: string;
  turn_id: string | null;
  at: number | null;
}

/** What one surface's card head may say about itself. */
export interface SurfaceHeadFacts {
  key: string;
  /** The panel's declared title. Never the plugin's id. */
  title: string;
  change: SurfaceChange | null;
}

export interface PanelData {
  key: string;
  /** The registry's own id for this registration, e.g. `gen-4`.
   *  Opaque: the real core mints it and there is no ordinal behind it,
   *  so it belongs in a tooltip, never in prose that counts. */
  generation_id: string | null;
  /** The panel's `reads`, in declaration order. A block names one by index. */
  reads: SourceResult[];
}

/** What `/api/uploads` answers: the stored file, under the id a message names it by. */
export interface UploadedFile {
  id: string;
  name: string;
  size: number;
  kind: string;
  mime: string;
}

/** A file a message carries: the upload's id, and the person's name for it. */
export interface GivenFile {
  id: string;
  name: string;
}

export async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    // Every call goes through `apiPath`: under a Hub the same shell is served
    // at `/agents/<id>/`, and a root-relative `/api/...` would ask the Hub
    // about no agent at all.
    response = await fetch(apiPath(path), {
      headers: init?.body ? { "content-type": "application/json" } : undefined,
      ...init,
    });
  } catch (err) {
    throw new ApiError(
      "连不上 workbench",
      err instanceof Error ? err.message : String(err),
      "浏览器没能连上本机的 workbench 进程。确认 python -m workbench serve 还在跑。",
      0,
      true,
    );
  }
  const text = await response.text();
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      throw new ApiError("收到了看不懂的应答", text.slice(0, 200), null, response.status, true);
    }
  }
  if (!response.ok)
    throw failureOf(response.status, body, { title: `HTTP ${response.status}`, detail: text.slice(0, 200), machine: true });
  return body as T;
}

/**
 * The `ApiError` a non-2xx answer carries, read the one way every door reads it (the Agent's, the Hub's, the
 * platform pages').
 *
 * An envelope gives its three sentences, the `code` and `key` a page branches on, and who the sentences were
 * written for: declared for the owner, or nobody said -- the second is the ordinary case and the reason the
 * default is this way round. An envelope with no `audience` (a route that has not declared) is the machine's.
 * A body with no envelope at all (a reverse proxy's HTML, a framework's own 422) gets the door's own `fallback`,
 * which says whether its sentences are the shell's or the wire's. URL prefixes, credentials and what a door does
 * on an expired session stay the door's.
 */
export function failureOf(
  status: number,
  body: unknown,
  fallback: { title: string; detail: string; machine: boolean },
): ApiError {
  const error = (body as { error?: ApiErrorBody & { next?: string | null } } | null)?.error;
  if (!error || typeof error !== "object") return new ApiError(fallback.title, fallback.detail, null, status, fallback.machine);
  const failure = new ApiError(
    error.title || fallback.title,
    error.detail || fallback.detail,
    error.hint || error.next || null,
    status,
    error.audience !== OWNER_AUDIENCE,
  );
  if (typeof error.code === "string") failure.code = error.code;
  if (typeof error.key === "string") failure.key = error.key;
  return failure;
}

/**
 * The spend banner (§7.81). Absent when no threshold is set: with no line
 * there is no question, and a permanent green bar is one people stop seeing.
 */
export interface SpendAlert {
  level: "near" | "over";
  /** The currency of the four amounts below; null when the Agent knows none, and then they are bare numbers. */
  currency: string | null;
  limit: number;
  settled: number;
  reserved: number;
  committed: number;
  ratio: number;
  warn_at: number;
}

/** One row of one of the five registries, as the capabilities page draws it. */
export interface CapabilityEntry {
  key: string;
  kind: string;
  plugin: string;
  generation_id: string | null;
  spec_hash: string | null;
  title: string;
  description: string;
  spec: Record<string, unknown>;
  /**
   * `ui_panels` only. The core stores a panel declaration and reads none of it
   * (§7.143), so this shell is the half that can refuse one -- and a panel
   * nobody can draw is a fact about this installation, not something to drop.
   * The field path and the reason, in Chinese.
   */
  problem?: string | null;
  /**
   * Channels only (§7.116). What the host found missing when it registered the
   * plugin, where to go and supply it, and the channel's own status tool if it
   * declared one -- 「已配置」 is not 「能用」.
   */
  needs_config?: string[];
  settings_panel?: string | null;
  status_tool?: string | null;
}

/** One channel's answer about itself, as its `<channel>.status` tool gave it. */
export interface ChannelStatus {
  obligation_id: string;
  ok: boolean;
  result: { state?: string; summary?: string } | null;
}

export interface CapabilityRegistry {
  kind: string;
  /** Set when the registry could not be read. Empty is not the same as absent. */
  problem: string | null;
  entries: CapabilityEntry[];
}

export interface RegistryChange {
  id: string;
  kind: string;
  key: string;
  action: string;
  plugin: string | null;
  generation_id: string | null;
  previous_generation_id: string | null;
  spec_hash: string | null;
  ts: number | null;
}

export interface Capabilities {
  registries: CapabilityRegistry[];
  history: RegistryChange[];
}

/** What became of one submission: the obligation, and the effects it opened. */
export interface CommandStatus {
  obligation_id: string;
  /** Original command metadata, retained independently of its continuation. */
  source?: string | null;
  known: boolean;
  state: string | null;
  action_state?: "running" | "held" | "completed" | "stopped" | "failed" | "unknown";
  /** Failure reported by the current continuation; not the approval answer. */
  error?: string | null;
  settled_at: number | null;
  effects: {
    id: string;
    tool: string | null;
    state: string | null;
    error: string | null;
    ts: number | null;
  }[];
  /**
   * The keys of the approval cards holding this command right now. On the
   * pushed `command` frame only (CHARTER §7.194); a fetched status derives
   * the same list from its held effects (`heldKeys` in `commandFeed.ts`).
   */
  held?: string[];
}

/**
 * What the server pushes when a command's status moves (CHARTER §7.194): the
 * status as `/api/commands/<obligation>` would answer it, plus the three facts
 * only the command row knows. One frame per change, none per row.
 */
export interface CommandFrame extends CommandStatus {
  command_id: string;
  panel_key: string | null;
  origin: string | null;
  held: string[];
}

export interface LastAgentView {
  at: number | null;
  conversations: { id: string; title: string; last_ts: number; partial: boolean;
    messages: { id: string; role: string; text: string; ts: number }[] }[];
  workspaces: { key: string; title: string; at: number;
    items: { id: string; title: string; status: string; state: string; context: Record<string, string> }[] }[];
  pending: { key: string; session_id: string; title: string; summary: string }[];
  requests: { id: string; text: string; ts: number }[];
}

export const api = {
  lastView: (signal?: AbortSignal) => request<LastAgentView>("/api/last-view", { signal }),
  surfacePreview: (key: string, body: Record<string, unknown>) => request<{ state: string; url?: string }>(
    `/api/panels/${encodeURIComponent(key)}/preview`, { method: "POST", body: JSON.stringify(body) }),
  bootstrap: (signal?: AbortSignal, observe = false) => request<Bootstrap>("/api/bootstrap", {
    signal, ...(observe ? { headers: { "X-PA-Runtime-Observe": "1" } } : {}),
  }),

  sessions: () => request<{ sessions: SessionSummary[] }>("/api/chat/sessions"),
  /** Whether this console draws a conversation: the one fact that decides where a page's way back leads. */
  conversationDrawn: (sessionId: string) =>
    request<{ session_id: string; owner_conversation: boolean }>(`/api/chat/${encodeURIComponent(sessionId)}/drawn`,
      { headers: { "X-PA-Runtime-Observe": "1" } }),
  chat: (sessionId: string, options?: { limit?: number; before?: string; signal?: AbortSignal }) =>
    request<{
      session_id: string; messages: ChatMessage[]; progress: TurnProgress | null; opening?: ConversationOpening;
      next_before?: string | null;
      /** True only when this opening is already on this conversation's record. */
      opening_recorded?: boolean;
      /** Work the Agent has going where this console does not speak (a schedule's), as record cards. */
      elsewhere?: ChatMessage[];
    }>(
      `/api/chat/${encodeURIComponent(sessionId)}${options?.limit ? `?${new URLSearchParams({ limit: String(options.limit), ...(options.before ? { before: options.before } : {}) })}` : ""}`,
      { signal: options?.signal, headers: { "X-PA-Runtime-Observe": "1" } },
    ),
  /**
   * One thing the Owner said.
   *
   * `page` and `panel_key` are where they were standing when they said it --
   * the same fact a page request records (`submit_message(request=)`), kept on
   * the submission so the Agent's plugin can read it after any reload. The
   * shell draws none of it.
   */
  send: (sessionId: string, text: string, options?: {
    page?: string; panel_key?: string; reply_id?: string; command_id?: string; files?: GivenFile[];
  }) => {
    return Promise.resolve(prepareChatScreen?.(sessionId)).then(screen => request<ChatReceipt>(
      `/api/chat/${encodeURIComponent(sessionId)}`,
      { method: "POST", body: JSON.stringify({ text, ...options, ...(screen ? { screen } : {}) }) },
    ));
  },
  /**
   * One file the Owner gives their Agent, sent as it is. The server decides
   * what it is from its bytes and answers with the id a message names it by;
   * the name rides along for showing only.
   */
  upload: (file: Blob, name: string, signal?: AbortSignal) =>
    request<UploadedFile>(`/api/uploads?${new URLSearchParams({ name })}`, {
      method: "POST", body: file, signal,
      headers: { "content-type": "application/octet-stream" },
    }),
  chatReceipt: (sessionId: string, commandId: string) =>
    request<ChatReceipt>(
      `/api/chat/${encodeURIComponent(sessionId)}/receipt?${new URLSearchParams({ command_id: commandId })}`,
      { headers: { "X-PA-Runtime-Observe": "1" } },
    ),
  stopChat: (sessionId: string, turnId: string, commandId: string) =>
    request<ChatStopStatus>(`/api/chat/${encodeURIComponent(sessionId)}/stop`, {
      method: "POST", body: JSON.stringify({ command_id: commandId, turn_id: turnId }),
    }),

  approvals: () =>
    request<{ pending: ApprovalCard[]; history: ApprovalCard[]; held: HeldEffect[] }>(
      "/api/approvals",
    ),
  /**
   * Answer one card. The key is the asking reply's id, not a handle: since
   * §7.139 ⑤ the question is a reply and the handle belongs to the effect,
   * which a card may not have found.
   *
   * CHARTER §7.135: this does not settle anything. It submits the option's
   * value as an ordinary input from this console's seat, and whoever owns the
   * question decides whether that counts.
   */
  resolveApproval: (key: string, decision: string, reason?: string) =>
    request<unknown>(`/api/approvals/${encodeURIComponent(key)}`, {
      method: "POST",
      body: JSON.stringify({ decision, reason }),
    }),

  timeline: (params: URLSearchParams, signal?: AbortSignal) =>
    request<TimelineResponse>(`/api/timeline?${params.toString()}`, { signal }),
  /** One L0 event whole, read from the Agent by its ref. */
  event: (ref: string, signal?: AbortSignal) =>
    request<{ event: LedgerEvent }>(`/api/event?${new URLSearchParams({ ref })}`, { signal }),
  /** The newest `limit` rows of one ledger, in `state` when given, and every state's count. */
  ledger: (name: string, options: { limit: number; state?: string | null }) =>
    request<{ ledger: string; rows: LedgerRow[]; counts: Record<string, number> }>(
      `/api/ledger/${encodeURIComponent(name)}?${new URLSearchParams({
        limit: String(options.limit),
        ...(options.state ? { state: options.state } : {}),
      })}`,
    ),

  capabilities: () => request<Capabilities>("/api/capabilities"),
  channelStatus: (channel: string) =>
    request<ChannelStatus>(
      `/api/capabilities/channels/${encodeURIComponent(channel)}/status`,
      { method: "POST" },
    ),
  commandStatus: (obligationId: string) =>
    request<CommandStatus>(`/api/commands/${encodeURIComponent(obligationId)}`),

  repairSurface: (key: string, block: number, context: Record<string, string>, detail: Record<string, unknown>, commandId?: string, automatic = false) =>
    request<{ command_id: string; obligation_id: string; session_id: string; version?: string }
      | { repairable: false; reason: string }>(
      `/api/panels/${encodeURIComponent(key)}/repair`,
      { method: "POST", body: JSON.stringify({ block, context, detail: surfaceDiagnostic(detail), command_id: commandId, ...(automatic ? { automatic: true } : {}) }) },
    ),

  panels: () => request<{ panels: PanelEntry[]; problems: PanelProblem[] }>("/api/panels"),
  panel: (key: string) =>
    request<{ key: string; plugin: string; spec: PanelSpec; generation_id?: string | null }>(
      `/api/panels/${encodeURIComponent(key)}`,
    ),
  panelData: (key: string, params?: Record<string, string>) => {
    const qs = params && Object.keys(params).length ? `?${new URLSearchParams(params)}` : "";
    return request<PanelData>(`/api/panels/${encodeURIComponent(key)}/data${qs}`);
  },
  panelAction: (key: string, action: unknown, values: Record<string, unknown>, extra?: {
    interaction?: { request_id: string; version: string; generation_id: string | null }; command_id?: string;
    /** The conversation the page is open in; the press lands there (§7.193). */
    session_id?: string;
    record_call_id?: string;
  }) =>
    request<{ command_id?: string; obligation_id?: string }>(
      `/api/panels/${encodeURIComponent(key)}/action`,
      { method: "POST", body: JSON.stringify({ action, values, ...extra }) },
    ),

  /**
   * The surface HTML and, under a Hub, its scoped resource bases.
   *
   * Not `request`: this answers HTML, not JSON, and the whole point of the
   * route is that the bytes are the plugin's. Failures still carry the three
   * lines, read out of the JSON body the error path does send.
   */
  panelUiFile: async (key: string, path: string, signal?: AbortSignal): Promise<SurfacePage> => {
    if (signal?.aborted) throw new DOMException("页面读取已取消", "AbortError");
    // Through `apiPath` like every other call: under a Hub this shell is
    // served at `/agents/<id>/`, and a root-relative `/api/panels/...` asks
    // the Hub about no agent at all (D70).
    const url = apiPath(
      `/api/panels/${encodeURIComponent(key)}/ui/${path
        .split("/")
        .map(encodeURIComponent)
        .join("/")}`,
    );
    const request = new AbortController();
    const cancel = () => request.abort();
    signal?.addEventListener("abort", cancel, { once: true });
    const timeout = new ApiError("页面读取超时", "这次界面资源未在 30 秒内读完。", "请再试一次。", 408);
    timeout.code = "resource_read_timeout";
    const reading = (async () => {
      let response: Response;
      try {
        response = await fetch(url, { signal: request.signal });
      } catch (err) {
        if (signal?.aborted) throw err;
        if (request.signal.aborted) throw timeout;
        throw new ApiError(
          "连不上 workbench",
          err instanceof Error ? err.message : String(err),
          "这一页的文件从 workbench 进程发出来。确认它还在跑。",
          0,
          true,
        );
      }
      const text = await response.text();
      if (!response.ok) {
        let error: ApiErrorBody | undefined;
        try {
          error = (JSON.parse(text) as { error?: ApiErrorBody }).error;
        } catch {
          error = undefined;
        }
        throw new ApiError(
          error?.title ?? `HTTP ${response.status}`,
          error?.detail ?? text.slice(0, 200),
          error?.hint ?? null,
          response.status,
          true,
        );
      }
      return { html: text, resources: surfaceResources(response.headers, key) };
    })();
    try {
      // Preserve read failures as outcomes: the shared deadline's fallback
      // applies only to a wait, not to an HTTP, network or body-read failure.
      const outcome = await withDeadline<{ page: SurfacePage } | { error: unknown }>(
        reading.then(page => ({ page }), error => ({ error })), { error: timeout }, 30_000,
      );
      if ("error" in outcome) {
        // Cancel only the expired resource READ; never retry a business press.
        if (outcome.error === timeout) request.abort();
        throw outcome.error;
      }
      return outcome.page;
    } finally {
      signal?.removeEventListener("abort", cancel);
    }
  },

  /**
   * One bridge call from a sandboxed surface (CHARTER §7.130).
   *
   * The browser is a relay here and knows it: `method` and `params` come from
   * the iframe verbatim, and every question about whether this surface may do
   * that is answered on the server against the registered declaration.
   */
  /** A page's own draft, kept beside its plugin's data so it outlives this tab. */
  surfaceState: (key: string, block: number, context: Record<string, string>, state: unknown) =>
    request<{ result: { saved: boolean; cleared: boolean } }>(
      `/api/panels/${encodeURIComponent(key)}/surface-state`,
      { method: "POST", body: JSON.stringify({ block, context, state }) },
    ),

  surfaceHead: (key: string) =>
    request<SurfaceHeadFacts>(`/api/panels/${encodeURIComponent(key)}/surface-head`),

  surfaceCall: (key: string, block: number, method: string, params: Record<string, unknown>, signal?: AbortSignal) =>
    request<{ result: unknown }>(`/api/panels/${encodeURIComponent(key)}/surface`, {
      method: "POST",
      ...(signal ? { signal } : {}),
      body: JSON.stringify({ block, method, params: method === "surface.error" ? surfaceDiagnostic(params) : params }),
    }),

  /**
   * Which showings of which pages this tab has in front of the owner now
   * (`lib/screen.ts`). `keepalive`, so the report a closing tab sends on its
   * way out still arrives.
   */
  screen: (report: ScreenReport) =>
    request<{ result: { recorded: boolean } }>("/api/screen", {
      method: "POST", body: JSON.stringify(report), keepalive: true,
    }),

  prefs: () => request<Record<string, unknown>>("/api/prefs"),
  savePrefs: (prefs: Record<string, unknown>) =>
    request<Record<string, unknown>>("/api/prefs", {
      method: "PUT",
      body: JSON.stringify(prefs),
    }),
};

/**
 * One agent in a Hub's directory, as the switcher draws it.
 *
 * The four fields below the name were already in every answer
 * (`hub/directory.py::describe`) and were read only by the Hub's own pages, so
 * an Agent's shell had no way to say why its own pages would not load: with the
 * Agent stopped it drew a spinner while the directory two clicks away said
 * 已停止 in the same second (D681). They are optional because `online` and
 * `location` are the only two the directory promises for every deployment --
 * `runner` exists for a cloud Agent the Hub supervises, and nothing else.
 */
export interface LocalClientInfo {
  id: string;
  name: string;
  workspace: string;
  online: boolean;
  last_seen: number | null;
  error?: string;
}

export interface LocalRuntimeState {
  state: "pending" | "preparing" | "running" | "stopping" | "stopped" | "error";
  applied_revision: number;
  error: string;
}

export interface HubAgent {
  message_delivery?: boolean;
  id: string;
  name: string;
  /** Where the process runs: the Hub's own word, drawn as given. */
  location: string;
  online: boolean;
  owner: string;
  /** A token has been minted: a local Agent has none until somebody pairs it. */
  paired?: boolean;
  /**
   * When this Agent last reached the Hub, and `null` for one that never has.
   *
   * Offline is not one state: an Agent that has worked and is away has records
   * this page cannot read right now, and one that has never arrived has none
   * and no fault to report. Absent on an older Hub, which is the same "no
   * record" answer.
   */
  last_connected_at?: number | null;
  client_id?: string | null;
  client?: LocalClientInfo | null;
  revision?: number;
  local_runtime?: LocalRuntimeState | null;
  /** The Owner's standing intent, not an observation of the process. */
  desired_running?: boolean;
  /** What the Hub's supervisor sees. Absent unless the Hub runs the process. */
  runner?: { state: string; restarts: number; last_error: string };
  /** Deployment lifecycle; automatic sleep preserves the Owner's running intent. */
  runtime?: {
    state: "disabled" | "sleeping" | "awake" | "starting" | "restarting" | "stopping";
    on_demand: boolean;
    next_wake_at: number | null;
    sleep_blockers: string[];
  };
}

/**
 * The Hub's agent directory, or `null` when there is no Hub.
 *
 * Not through `request`: this is the one call that is *not* about an agent, so
 * it never takes the agent prefix, and its absence is an ordinary answer rather
 * than an error worth a red page.
 *
 * A 404 is the documented "no Hub here". A 200 is not enough on its own:
 * `pa-workbench serve` answers every unknown path with the SPA's index.html and
 * status 200, so a switcher built on the status code alone would appear on
 * single-agent installs pointing at nothing. The body has to be JSON carrying
 * an `agents` array before anything is drawn.
 */
export async function hubAgents(): Promise<HubAgent[] | null> {
  try {
    const response = await fetch("/api/hub/agents", {
      headers: { accept: "application/json" },
    });
    if (!response.ok) return null;
    const body = (await response.json()) as { agents?: unknown };
    if (!Array.isArray(body?.agents)) return null;
    return body.agents.filter(
      (agent): agent is HubAgent =>
        typeof agent === "object" && agent !== null && typeof (agent as HubAgent).id === "string",
    );
  } catch {
    // No Hub, no network, or index.html where JSON was expected. All three
    // mean the same thing to this shell: there is nothing to switch between.
    return null;
  }
}

/**
 * A conversation moved (`chat` on the stream, handed to `chatFeed`).
 *
 * `session_id` is the conversation, or null when any of them may have. When a
 * streamed chunk is all that moved, the frame tells that one bubble instead of
 * sending the page to read: the whole of it (`bubble`, as `/api/chat` draws it)
 * when it is new or changed, or only what `grew` at the end of the text of the
 * bubble `id`. Each telling of a stream is numbered (`stream_seq`); a growth is
 * appended only onto the telling just before it. `progress` rides along when
 * the turn's progress line moved (`/api/chat`'s own `progress`).
 */
export interface ChatFrame {
  session_id: string | null;
  bubble?: ChatMessage | null;
  id?: string;
  stream_seq?: number;
  grew?: string;
  progress?: TurnProgress | null;
}

export type StreamEvent =
  | { event: "connection"; data: Connection }
  | { event: "panels"; data: { panels: PanelEntry[]; record_panels: RecordPanel[]; presentations?: PanelPresentation[] } }
  | { event: "approvals"; data: { pending: ApprovalCard[] } }
  | { event: "chat"; data: ChatFrame }
  | { event: "owner"; data: OwnerState }
  /** A row was folded. Nothing of it: the pages count these and read their own tables. */
  | { event: "ledger"; data: null }
  | { event: "command"; data: CommandFrame }
  | { event: "spend"; data: SpendAlert | null }
  | { event: "capabilities"; data: Capabilities }
  /**
   * A surface's own files changed on disk (CHARTER §7.130 phase 2). One
   * signature per panel that has a surface block; the page reassembles itself
   * when its own signature moves. Pushed rather than polled, and it costs the
   * ledger nothing — the server watched the filesystem, not the record.
   */
  | { event: "surfaces"; data: { versions: Record<string, string> } }
  | { event: "rebuilt"; data: { events: number; commands: number } }
  /** A Hub's directory row for this Agent changed: its name. */
  | { event: "directory"; data: { name: string } }
  /** Settings were written in this namespace; which keys and values is the setting's own read. */
  | { event: "settings"; data: { namespace: string | null } }
  /**
   * A tool that is not declared `effect: read` just finished, or a child
   * turn just started or closed (`liquid-page-runtime.md` §1's third
   * unsolicited re-read trigger). No identity of what ran, no plugin name -- just
   * "something other than looking happened in this session" -- because a
   * page's own re-read goes through the read door, which writes no row and
   * never raises this, which is what keeps the loop from feeding itself.
   */
  | { event: "work"; data: { session_id: string | null } };

/**
 * One EventSource for the whole app. If it drops, the browser is out of date
 * and says so; it does not pretend the last snapshot is live.
 */
export function openStream(
  onEvent: (event: StreamEvent) => void,
  onBrowserLinkChange: (up: boolean) => void,
): () => void {
  let source: EventSource | null = null;
  let closed = false;
  const names: StreamEvent["event"][] = [
    "connection",
    "panels",
    "approvals",
    "owner",
    "chat",
    "ledger",
    "command",
    "spend",
    "capabilities",
    "surfaces",
    "rebuilt",
    "directory",
    "work",
    "settings",
  ];

  const connect = () => {
    if (closed) return;
    source = new EventSource(apiPath("/api/stream"));
    source.onopen = () => onBrowserLinkChange(true);
    source.onerror = () => {
      onBrowserLinkChange(false);
      // EventSource retries on its own; do not stack a second one on top.
    };
    for (const name of names) {
      source.addEventListener(name, (raw) => {
        try {
          onEvent({ event: name, data: JSON.parse((raw as MessageEvent).data) } as StreamEvent);
        } catch {
          /* a frame we cannot parse is dropped, not guessed at */
        }
      });
    }
  };

  connect();
  return () => {
    closed = true;
    source?.close();
  };
}
